// floorStackApi — typed fetchers for /v1/sites/:id/floor-stack +
// /v1/sites/:id/floors/:fid/command-view + buildings/floors/layouts/markers CRUD.
//
// Mirrors the response shapes in @nexus/shared/types but we redefine
// the runtime-relevant slices here so the frontend doesn't have to bundle
// the worker types module (Vite workspace deduping doesn't always cover
// type-only imports cleanly).

import { apiFetch, API_BASE, resolveApiBase } from "../../lib/api-client.js";

// ── Domain types ─────────────────────────────────────────────────────────

export type FloorSummaryStatus =
  | "idle" | "normal" | "attention" | "critical" | "disabled";

export type LayoutSourceKind = "svg" | "png" | "pdf" | "manual";

export type MarkerType =
  | "location" | "route_node" | "incident_pin" | "note" | "camera" | "aed" | "exit";

export interface BuildingDto {
  id: string;
  tenant_id: string;
  site_id: string;
  name: string;
  display_order: number;
  floors_count: number;
  floors_below_ground_count: number;
  brand_primary: string | null;
  description: string | null;
  status: "active" | "archived";
  created_at: string;
  updated_at: string;
}

export interface BuildingFloorDto {
  id: string;
  tenant_id: string;
  building_id: string;
  site_id: string;
  floor_no: number;
  floor_name: string;
  display_label: string | null;
  display_order: number;
  status: "active" | "disabled" | "archived";
  is_critical: 0 | 1;
  layout_id: string | null;
  last_status_color: FloorSummaryStatus;
  last_patrol_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface FloorStatusSnapshotDto {
  id: string;
  tenant_id: string;
  site_id: string;
  building_id: string;
  floor_id: string;
  snapshot_at: string;
  location_total: number;
  completed: number;
  pending: number;
  overdue: number;
  incident_warning_count: number;
  incident_critical_count: number;
  online_officers: number;
  active_sessions: number;
  completion_rate: number;
  summary_status: FloorSummaryStatus;
  last_patrol_at: string | null;
  notes: string | null;
  created_at: string;
}

export interface MapLayoutDto {
  id: string;
  tenant_id: string;
  floor_id: string;
  current_version_id: string | null;
  status: "draft" | "published" | "archived";
  created_at: string;
  updated_at: string;
}

export interface MapLayoutVersionDto {
  id: string;
  tenant_id: string;
  layout_id: string;
  version_no: number;
  source_kind: LayoutSourceKind;
  source_r2_key: string | null;
  source_content_type: string | null;
  source_size_bytes: number | null;
  source_sha256: string | null;
  normalized_json: string;
  viewbox_json: string | null;
  uploaded_by_user_id: string | null;
  notes: string | null;
  status: "draft" | "published" | "archived";
  created_at: string;
  published_at: string | null;
  parse_status?: "manual" | "pending" | "success" | "failed";
  parse_started_at?: string | null;
  parse_completed_at?: string | null;
  parse_cost_usd?: number | null;
  parse_model?: string | null;
  parse_error?: string | null;
  edited_at?: string | null;
  edited_by_user_id?: string | null;
}

// ── Normalized layout schema (mirrors packages/shared/src/openai/layout-parser.ts) ──

export interface LayoutPoint { x: number; y: number; }
export interface LayoutWall {
  id: string;
  kind: "exterior" | "interior";
  points: [LayoutPoint, LayoutPoint];
  thickness_px?: number | null;
}
export interface LayoutRoom {
  id: string;
  vertices: LayoutPoint[];
  label: string | null;
  kind:
    | "office" | "meeting" | "lobby" | "corridor" | "stair"
    | "elevator" | "restroom" | "utility" | "storage" | "exit"
    | "outdoor" | "unknown";
}
export interface LayoutDoor {
  id: string;
  hinge: LayoutPoint;
  swing_to: LayoutPoint;
  label: string | null;
}
export interface LayoutCorridor {
  id: string;
  centerline: LayoutPoint[];
  width_px: number;
}
export interface LayoutEntryPoint {
  id: string;
  point: LayoutPoint;
  kind: "main" | "fire_exit" | "service" | "loading" | "elevator" | "stair" | "other";
  label: string | null;
}
export interface LayoutLabel {
  id: string;
  point: LayoutPoint;
  text: string;
  font_size_px: number;
  rotation_deg: number;
}

export interface NormalizedLayoutDto {
  schema_version: 1;
  image_size: { width: number; height: number };
  scale_meters_per_pixel: number | null;
  walls: LayoutWall[];
  rooms: LayoutRoom[];
  doors: LayoutDoor[];
  corridors: LayoutCorridor[];
  entry_points: LayoutEntryPoint[];
  labels: LayoutLabel[];
}

export const EMPTY_LAYOUT: NormalizedLayoutDto = {
  schema_version: 1,
  image_size: { width: 1000, height: 700 },
  scale_meters_per_pixel: null,
  walls: [],
  rooms: [],
  doors: [],
  corridors: [],
  entry_points: [],
  labels: [],
};

export interface MapMarkerDto {
  id: string;
  tenant_id: string;
  layout_id: string;
  layout_version_id: string;
  floor_id: string;
  marker_type: MarkerType;
  linked_location_id: string | null;
  position_x: number;
  position_y: number;
  rotation_deg: number;
  label: string | null;
  payload_json: string;
  status: "active" | "archived";
  created_at: string;
  updated_at: string;
}

export interface FloorStackResponse {
  site: {
    id: string;
    tenant_id: string;
    name: string;
    address: string | null;
    timezone: string;
    status: string;
    risk_level: string;
    created_at: string;
    updated_at: string;
    default_building_id: string | null;
    brand_primary: string | null;
  };
  buildings: Array<BuildingDto & {
    floors: Array<BuildingFloorDto & { latest_snapshot: FloorStatusSnapshotDto | null }>;
  }>;
  fetched_at: string;
  correlation_id: string | null;
}

export interface CommandViewResponse {
  floor: BuildingFloorDto;
  building: BuildingDto;
  site: {
    id: string;
    tenant_id: string;
    name: string;
    address: string | null;
    timezone: string;
    status: string;
    risk_level: string;
    created_at: string;
    updated_at: string;
  };
  layout: { id: string; current_version: MapLayoutVersionDto | null } | null;
  markers: MapMarkerDto[];
  routes: Array<{
    id: string;
    name: string;
    site_id: string;
    cadence_minutes: number | null;
    expected_duration_seconds: number | null;
    locations: Array<{
      route_id: string;
      location_id: string;
      sequence: number;
      expected_dwell_seconds: number | null;
      location: { id: string; code: string; label: string; floor_id: string | null };
      marker: MapMarkerDto | null;
    }>;
  }>;
  active_incidents: Array<{
    id: string;
    severity: string;
    status: string;
    title: string;
    body: string;
    floor_id: string | null;
    position_x: number | null;
    position_y: number | null;
    created_at: string;
  }>;
  latest_snapshot: FloorStatusSnapshotDto | null;
  fetched_at: string;
  correlation_id: string | null;
}

// ── Errors ───────────────────────────────────────────────────────────────

export class FloorStackApiError extends Error {
  status: number;
  code: string;
  correlationId: string | null;
  fields?: string[];
  upstreamMessage?: string;
  constructor(status: number, code: string, correlationId: string | null, fields?: string[], upstreamMessage?: string) {
    super(code);
    this.status = status;
    this.code = code;
    this.correlationId = correlationId;
    this.fields = fields;
    this.upstreamMessage = upstreamMessage;
  }
}

export function friendlyFloorStackError(err: unknown): string {
  if (err instanceof FloorStackApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403) return "您目前的角色不能執行這個操作。";
    if (err.status === 404) {
      if (err.code === "no_layout") return "這層樓尚未上傳圖資。";
      if (err.code === "site_not_found") return "找不到此案場。";
      if (err.code === "floor_not_found") return "找不到此樓層。";
      return "找不到資料。";
    }
    if (err.status === 413) return "上傳檔案超過 30MB 上限。";
    if (err.status === 503 && err.code === "layouts_bucket_unbound")
      return "圖資儲存尚未啟用，請聯繫平台管理員。";
    if (err.status === 400) {
      if (err.code === "invalid_source_kind") return "圖資格式不支援，僅接受 SVG / PNG / PDF。";
      if (err.code === "tenant_id_required") return "尚未選定操作的租戶。";
      if (err.code === "empty_body") return "上傳檔案為空。";
      if (err.code === "invalid_body") {
        const f = (err.fields ?? []).join("、");
        return `欄位填寫不完整：${f}。`;
      }
    }
  }
  return "資料暫時無法存取，請稍後再試。";
}

async function asError(r: Response): Promise<FloorStackApiError> {
  let code = "request_failed";
  let fields: string[] | undefined;
  let cid: string | null = r.headers.get("x-correlation-id");
  let msg: string | undefined;
  try {
    const body = (await r.json()) as { error?: string; correlation_id?: string; fields?: string[]; message?: string };
    code = body.error ?? code;
    cid = body.correlation_id ?? cid;
    fields = body.fields;
    msg = body.message;
  } catch {
    // ignore non-JSON body
  }
  return new FloorStackApiError(r.status, code, cid, fields, msg);
}

function tenantQuery(tenantId: string | null | undefined): string {
  return tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
}

// ── Composite reads ──────────────────────────────────────────────────────

export async function getFloorStack(siteId: string, tenantId: string | null = null): Promise<FloorStackResponse> {
  const r = await apiFetch(`/v1/sites/${encodeURIComponent(siteId)}/floor-stack${tenantQuery(tenantId)}`, {
    credentials: "include",
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as FloorStackResponse;
}

export async function getCommandView(siteId: string, floorId: string, tenantId: string | null = null): Promise<CommandViewResponse> {
  const r = await apiFetch(`/v1/sites/${encodeURIComponent(siteId)}/floors/${encodeURIComponent(floorId)}/command-view${tenantQuery(tenantId)}`, {
    credentials: "include",
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as CommandViewResponse;
}

// ── Buildings CRUD ───────────────────────────────────────────────────────

export interface CreateBuildingBody {
  site_id: string;
  name: string;
  display_order?: number;
  floors_count?: number;
  floors_below_ground_count?: number;
  brand_primary?: string;
  description?: string;
  set_as_default?: boolean;
}

export async function createBuilding(body: CreateBuildingBody, tenantId: string | null = null): Promise<{ id: string }> {
  const r = await apiFetch(`/v1/buildings${tenantQuery(tenantId)}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { id: string };
}

export async function listBuildings(siteId: string, tenantId: string | null = null): Promise<BuildingDto[]> {
  const params = new URLSearchParams();
  if (tenantId) params.set("tenant_id", tenantId);
  if (siteId) params.set("site_id", siteId);
  const r = await apiFetch(`/v1/buildings?${params.toString()}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return ((await r.json()) as { items: BuildingDto[] }).items;
}

/** Tenant-wide buildings list (no site_id filter). Used by RBAC scope
 *  picker so admins can grant a role limited to a flat set of buildings
 *  spanning multiple sites. */
export async function listAllBuildings(tenantId: string | null = null): Promise<BuildingDto[]> {
  const params = new URLSearchParams();
  if (tenantId) params.set("tenant_id", tenantId);
  const r = await apiFetch(`/v1/buildings${params.toString() ? `?${params.toString()}` : ""}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return ((await r.json()) as { items: BuildingDto[] }).items;
}

export interface PatchBuildingBody {
  name?: string;
  display_order?: number;
  floors_count?: number;
  floors_below_ground_count?: number;
  brand_primary?: string | null;
  description?: string | null;
  status?: "active" | "archived";
}

/** PATCH a building. Server: apps/tenant-api/src/floor-stack.ts:460
 *  (PATCH /v1/buildings/:id). Added 2026-05 for AI Plan & Apply Phase
 *  3 dispatcher's patch_building case — also usable from any future UI
 *  edit form. Mirrors patchFloor's signature shape. */
export async function patchBuilding(id: string, patch: PatchBuildingBody, tenantId: string | null = null): Promise<void> {
  const r = await apiFetch(`/v1/buildings/${encodeURIComponent(id)}${tenantQuery(tenantId)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify(patch),
  });
  if (!r.ok) throw await asError(r);
}

// ── Floors CRUD ──────────────────────────────────────────────────────────

export interface CreateFloorBody {
  building_id: string;
  floor_no: number;
  floor_name: string;
  display_label?: string;
  display_order?: number;
  is_critical?: boolean;
}

export async function createFloor(body: CreateFloorBody, tenantId: string | null = null): Promise<{ id: string }> {
  const r = await apiFetch(`/v1/floors${tenantQuery(tenantId)}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { id: string };
}

export async function patchFloor(id: string, patch: Partial<{ floor_name: string; display_label: string | null; display_order: number; status: "active" | "disabled" | "archived"; is_critical: boolean }>, tenantId: string | null = null): Promise<void> {
  const r = await apiFetch(`/v1/floors/${encodeURIComponent(id)}${tenantQuery(tenantId)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify(patch),
  });
  if (!r.ok) throw await asError(r);
}

// ── Layout binary upload ────────────────────────────────────────────────

export interface LayoutUploadResult {
  layout_id: string;
  version_id: string;
  version_no: number;
  r2_key: string;
  sha256: string;
  size_bytes: number;
}

export interface ParseLayoutResult {
  ok: true;
  layout_id: string;
  version_id: string;
  r2_key: string;
  parse_status: "success";
  parse_cost_usd: number;
  normalized: NormalizedLayoutDto;
  usage: { prompt_tokens: number; completion_tokens: number; total_tokens: number; cost_usd: number; model: string };
}

export interface ParseLayoutFailure {
  ok: false;
  error: "parse_failed";
  code: string;
  /** Human-readable zh-TW from backend (already includes upstream_message when relevant). */
  message: string;
  /** Raw OpenAI error.message when the failure originated upstream. Null otherwise. */
  upstream_message?: string | null;
  layout_id: string | null;
  version_id: string | null;
}

/**
 * Upload progress event surfaced to the editor's progress bar + AI-thinking
 * timer. Two phases:
 *   - "uploading"  — XHR.upload byte progress (0..1)
 *   - "ai_parsing" — bytes are at the worker; we're now blocking on the
 *                    OpenAI call. Frontend animates an elapsed timer
 *                    against `expectedSecondsTotal`.
 */
export interface ParseUploadProgress {
  phase: "uploading" | "ai_parsing";
  /** 0..1 upload completion. Pinned to 1 once we enter ai_parsing. */
  bytesPercent: number;
  bytesLoaded: number;
  bytesTotal: number;
}

export interface ParseLayoutOptions {
  notes?: string | null;
  onProgress?: (p: ParseUploadProgress) => void;
  signal?: AbortSignal;
}

/**
 * Submit a fresh image for ChatGPT-vision parsing. Backend uploads to R2,
 * creates a draft version, then runs the OpenAI vision model (default
 * gpt-5.5, configurable via the LAYOUT_PARSER_MODEL platform secret) and
 * writes the normalized JSON onto that version.
 *
 * Implementation note: we use XMLHttpRequest instead of fetch because
 * fetch() can't surface request-body upload progress in browsers. The
 * caller's `onProgress` is fed the byte transfer % during the upload
 * phase, then a single "ai_parsing" event the moment upload finishes —
 * the editor uses that signal to swap from a real progress bar to an
 * elapsed-time animation while the worker is calling OpenAI.
 *
 * If parsing fails the version is still saved (so the user can re-parse
 * or hand-edit) and a friendly error is surfaced.
 */
export async function parseLayoutFromImage(
  floorId: string,
  file: File | Blob,
  tenantId: string | null = null,
  options: ParseLayoutOptions = {},
): Promise<ParseLayoutResult | ParseLayoutFailure> {
  const fd = new FormData();
  fd.append("image", file);
  if (options.notes) fd.append("notes", options.notes);
  const params = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const url = `${API_BASE}/v1/floors/${encodeURIComponent(floorId)}/layout-parse${params}`;
  return uploadWithProgress(url, fd, options);
}

function uploadWithProgress(
  url: string,
  body: FormData,
  options: ParseLayoutOptions,
): Promise<ParseLayoutResult | ParseLayoutFailure> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url, true);
    xhr.withCredentials = true;
    xhr.responseType = "text";

    if (options.signal) {
      if (options.signal.aborted) {
        reject(new FloorStackApiError(0, "aborted", null));
        return;
      }
      options.signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }

    let total = 0;
    xhr.upload.addEventListener("progress", (e) => {
      total = e.lengthComputable ? e.total : total;
      const loaded = e.loaded;
      const percent = total > 0 ? Math.min(loaded / total, 1) : 0;
      options.onProgress?.({
        phase: "uploading",
        bytesPercent: percent,
        bytesLoaded: loaded,
        bytesTotal: total,
      });
    });
    xhr.upload.addEventListener("load", () => {
      // Bytes are all at the gateway; from here we're waiting on OpenAI.
      options.onProgress?.({
        phase: "ai_parsing",
        bytesPercent: 1,
        bytesLoaded: total,
        bytesTotal: total,
      });
    });

    xhr.addEventListener("load", () => {
      const cid = xhr.getResponseHeader("x-correlation-id");
      const status = xhr.status;
      const text = xhr.responseText;
      // Soft-failure shapes (worker validated request but parse step failed) —
      // 502 (R2 / upstream) and 503 (OpenAI not configured / network).
      if (status === 502 || status === 503) {
        let body: Partial<ParseLayoutFailure> = {};
        try { body = JSON.parse(text) as Partial<ParseLayoutFailure>; } catch { /* keep empty */ }
        resolve({
          ok: false,
          error: "parse_failed",
          code: body.code ?? "internal",
          message: body.message ?? "圖片解析暫時無法完成，請稍後再試或改用人工編輯。",
          upstream_message: body.upstream_message ?? null,
          layout_id: body.layout_id ?? null,
          version_id: body.version_id ?? null,
        });
        return;
      }
      if (status >= 200 && status < 300) {
        try {
          resolve(JSON.parse(text) as ParseLayoutResult);
        } catch (e) {
          reject(new FloorStackApiError(status, "invalid_json", cid));
        }
        return;
      }
      // Hard error — bubble up so the caller's catch() can map to friendly text.
      let code = "request_failed";
      try {
        const body = JSON.parse(text) as { error?: string; code?: string };
        code = body.error ?? body.code ?? code;
      } catch { /* ignore */ }
      reject(new FloorStackApiError(status, code, cid));
    });
    xhr.addEventListener("error", () => {
      reject(new FloorStackApiError(0, "network", xhr.getResponseHeader("x-correlation-id")));
    });
    xhr.addEventListener("abort", () => {
      reject(new FloorStackApiError(0, "aborted", xhr.getResponseHeader("x-correlation-id")));
    });
    xhr.addEventListener("timeout", () => {
      reject(new FloorStackApiError(0, "timeout", xhr.getResponseHeader("x-correlation-id")));
    });

    xhr.send(body);
  });
}

/** Re-parse an already-uploaded version (no new file). */
export async function reparseLayoutVersion(
  floorId: string,
  versionId: string,
  tenantId: string | null = null,
): Promise<ParseLayoutResult | ParseLayoutFailure> {
  const params = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/floors/${encodeURIComponent(floorId)}/layout-parse${params}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ layout_version_id: versionId }),
  });
  if (r.status === 502 || r.status === 503) {
    const body = (await r.json().catch(() => ({}))) as ParseLayoutFailure;
    return { ...body, ok: false, error: "parse_failed", code: body.code ?? "internal" };
  }
  if (!r.ok) throw await asError(r);
  return ((await r.json()) as ParseLayoutResult);
}

/** Persist user-edited normalized JSON back onto an existing version. */
export async function saveLayoutEdits(
  layoutId: string,
  versionId: string,
  normalized: NormalizedLayoutDto,
  tenantId: string | null = null,
  opts?: { notes?: string },
): Promise<{ ok: true; version_id: string; edited_at: string }> {
  const params = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/layouts/${encodeURIComponent(layoutId)}/versions/${encodeURIComponent(versionId)}/save-edits${params}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      normalized,
      viewbox: { x: 0, y: 0, width: normalized.image_size.width, height: normalized.image_size.height },
      notes: opts?.notes ?? null,
    }),
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { ok: true; version_id: string; edited_at: string };
}

export async function uploadLayout(
  floorId: string,
  file: File,
  sourceKind: LayoutSourceKind,
  tenantId: string | null = null,
): Promise<LayoutUploadResult> {
  const params = new URLSearchParams();
  if (tenantId) params.set("tenant_id", tenantId);
  params.set("source_kind", sourceKind);
  params.set("content_type", file.type || "application/octet-stream");
  const r = await apiFetch(`/v1/floors/${encodeURIComponent(floorId)}/layout-upload?${params.toString()}`, {
    method: "POST",
    headers: { "content-type": file.type || "application/octet-stream" },
    credentials: "include",
    body: file,
  });
  if (!r.ok) throw await asError(r);
  return ((await r.json()) as { layout_id: string; version_id: string; version_no: number; r2_key: string; sha256: string; size_bytes: number });
}

// ── Layouts: read / publish / version metadata ──────────────────────────

export async function getFloorLayout(floorId: string, tenantId: string | null = null): Promise<{ layout: MapLayoutDto; current_version: MapLayoutVersionDto | null } | null> {
  const r = await apiFetch(`/v1/floors/${encodeURIComponent(floorId)}/layout${tenantQuery(tenantId)}`, { credentials: "include" });
  if (r.status === 404) return null;
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { layout: MapLayoutDto; current_version: MapLayoutVersionDto | null };
}

/**
 * Clear (reset) a floor's base map. Archives every layout version on
 * the floor's layout, drops `map_layouts.current_version_id`, and
 * detaches the floor so the next upload bootstraps a fresh layout
 * row. Markers placed on now-archived versions are preserved — the
 * next successful parse migrates them onto the new current version
 * (PR #137 carry-over). Operator-visible action behind 「清除底圖」.
 */
export async function clearFloorLayout(floorId: string, tenantId: string | null = null): Promise<{ ok: true; layout_id?: string; archived_versions?: number; already_empty?: boolean }> {
  const r = await apiFetch(`/v1/floors/${encodeURIComponent(floorId)}/layout${tenantQuery(tenantId)}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { ok: true; layout_id?: string; archived_versions?: number; already_empty?: boolean };
}

export async function listLayoutVersions(layoutId: string, tenantId: string | null = null): Promise<MapLayoutVersionDto[]> {
  const r = await apiFetch(`/v1/layouts/${encodeURIComponent(layoutId)}/versions${tenantQuery(tenantId)}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return ((await r.json()) as { items: MapLayoutVersionDto[] }).items;
}

export async function publishLayoutVersion(layoutId: string, versionId: string, tenantId: string | null = null): Promise<void> {
  const r = await apiFetch(`/v1/layouts/${encodeURIComponent(layoutId)}/versions/${encodeURIComponent(versionId)}/publish${tenantQuery(tenantId)}`, {
    method: "POST",
    credentials: "include",
  });
  if (!r.ok) throw await asError(r);
}

export function layoutSourceUrl(layoutId: string, versionId: string, tenantId: string | null = null): string {
  const params = new URLSearchParams();
  if (tenantId) params.set("tenant_id", tenantId);
  const base = resolveApiBase();
  return `${base}/v1/layouts/${encodeURIComponent(layoutId)}/versions/${encodeURIComponent(versionId)}/source${params.toString() ? "?" + params.toString() : ""}`;
}

// ── Markers CRUD ─────────────────────────────────────────────────────────

export interface CreateMarkerBody {
  marker_type: MarkerType;
  position_x: number;
  position_y: number;
  rotation_deg?: number;
  label?: string;
  linked_location_id?: string;
  payload?: unknown;
}

export async function listMarkers(layoutId: string, versionId: string, tenantId: string | null = null): Promise<MapMarkerDto[]> {
  const r = await apiFetch(`/v1/layouts/${encodeURIComponent(layoutId)}/versions/${encodeURIComponent(versionId)}/markers${tenantQuery(tenantId)}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return ((await r.json()) as { items: MapMarkerDto[] }).items;
}

export async function createMarker(layoutId: string, versionId: string, body: CreateMarkerBody, tenantId: string | null = null): Promise<{ id: string }> {
  const r = await apiFetch(`/v1/layouts/${encodeURIComponent(layoutId)}/versions/${encodeURIComponent(versionId)}/markers${tenantQuery(tenantId)}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { id: string };
}

export async function patchMarker(id: string, patch: Partial<{ position_x: number; position_y: number; rotation_deg: number; label: string | null; linked_location_id: string | null; payload: unknown; status: "active" | "archived" }>, tenantId: string | null = null): Promise<void> {
  const r = await apiFetch(`/v1/markers/${encodeURIComponent(id)}${tenantQuery(tenantId)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify(patch),
  });
  if (!r.ok) throw await asError(r);
}

export async function archiveMarker(id: string, tenantId: string | null = null): Promise<void> {
  const r = await apiFetch(`/v1/markers/${encodeURIComponent(id)}${tenantQuery(tenantId)}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!r.ok) throw await asError(r);
}
